import { EditKeyPermissionsDialog } from '@/components/EditKeyPermissionsDialog';
import { IssueKeyDialog } from '@/components/IssueKeyDialog';
import { KeyPermissionsCell } from '@/components/KeyPermissionsCell';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useParams } from 'react-router';
import {
  fetchPartner,
  fetchPartnerKeys,
  issuePartnerKey,
  revokePartnerKey,
  setPartnerKeyPermissions,
  setPartnerStatus,
} from '@/api/partners';
import type { ApiKeyView, OperatingMode, PartnerStatus } from '@/api/types';
import { useCan } from '@/auth/session';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DataTable } from '@/ui/DataTable';
import { PageHeader } from '@/ui/PageHeader';
import { PageSpinner } from '@/ui/Spinner';
import { QueryError } from '@/ui/QueryError';
import { OperatingModeDialog } from './OperatingModeDialog';

const STATUS_TONE: Record<PartnerStatus, 'secondary' | 'outline' | 'destructive'> = {
  ACTIVE: 'secondary',
  SUSPENDED: 'outline',
  CLOSED: 'destructive',
};

const MODE_TONE: Record<OperatingMode, 'default' | 'outline'> = {
  OPERATOR: 'default',
  DELEGATED: 'outline',
};

export function PartnerDetailPage() {
  const { partnerId = '' } = useParams();
  const can = useCan();
  const queryClient = useQueryClient();
  const [issueOpen, setIssueOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);
  const [editing, setEditing] = useState<ApiKeyView | null>(null);

  const detail = useQuery({
    queryKey: ['partners', partnerId],
    queryFn: () => fetchPartner(partnerId),
  });
  const keys = useQuery({
    queryKey: ['partners', partnerId, 'keys'],
    queryFn: () => fetchPartnerKeys(partnerId),
  });

  const changeStatus = useMutation({
    mutationFn: (status: PartnerStatus) => setPartnerStatus(partnerId, status),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['partners'] }),
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) => revokePartnerKey(partnerId, keyId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['partners', partnerId, 'keys'] }),
  });

  if (detail.isPending) return <PageSpinner label="Loading partner" />;
  if (detail.isError)
    return <QueryError error={detail.error} onRetry={() => void detail.refetch()} />;

  const { partner } = detail.data;
  const canManage = can('partners.manage');
  const canManageKeys = can('partnerKeys.manage');
  const nextMode: OperatingMode = partner.operating_mode === 'OPERATOR' ? 'DELEGATED' : 'OPERATOR';

  const keyColumns: ColumnDef<ApiKeyView, never>[] = [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'environment', header: 'Environment' },
    { accessorKey: 'key_prefix', header: 'Key prefix' },
    {
      accessorKey: 'permissions',
      header: 'Permissions',
      cell: ({ row }) => (
        <KeyPermissionsCell
          apiKey={row.original}
          allowed={partner.allowed_key_permissions}
          operatingMode={partner.operating_mode}
        />
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'ACTIVE' ? 'secondary' : 'outline'}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        canManageKeys && row.original.status === 'ACTIVE' ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(row.original)}>
              Edit permissions
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(row.original.id)}
            >
              Revoke
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader title={partner.name} description={partner.slug} />

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>{partner.type === 'SYSTEM' ? 'System partner' : 'Reseller'}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={STATUS_TONE[partner.status]}>{partner.status}</Badge>
            <Badge variant={MODE_TONE[partner.operating_mode]}>{partner.operating_mode}</Badge>
            {canManage && !partner.system && (
              <Select
                value={partner.status}
                onValueChange={(value) => changeStatus.mutate(value as PartnerStatus)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="SUSPENDED">Suspended</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
            )}
            {can('partners.operatingMode') && (
              <Dialog open={modeOpen} onOpenChange={setModeOpen}>
                {partner.system ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0}>
                          <Button variant="outline" disabled>
                            Switch to {nextMode} mode
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        The system partner is always in DELEGATED mode.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <DialogTrigger asChild>
                    <Button variant="outline">Switch to {nextMode} mode</Button>
                  </DialogTrigger>
                )}
                {modeOpen && (
                  <OperatingModeDialog
                    partnerId={partnerId}
                    partnerName={partner.name}
                    mode={nextMode}
                    onDone={() => setModeOpen(false)}
                  />
                )}
              </Dialog>
            )}
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>API keys</CardTitle>
          {canManageKeys && keys.data && (
            <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
              <DialogTrigger asChild>
                <Button>Issue key</Button>
              </DialogTrigger>
              <IssueKeyDialog
                key={String(issueOpen)}
                issueKey={(request) => issuePartnerKey(partnerId, request)}
                invalidateKey={['partners', partnerId, 'keys']}
                availablePermissions={keys.data.available_permissions}
                operatingMode={partner.operating_mode}
                onDone={() => setIssueOpen(false)}
              />
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {keys.isError ? (
            <QueryError error={keys.error} onRetry={() => void keys.refetch()} />
          ) : (
            <DataTable
              columns={keyColumns}
              data={keys.data?.keys ?? []}
              getRowId={(row) => row.id}
              isLoading={keys.isPending}
              empty="No API keys yet."
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        {editing && keys.data && (
          <EditKeyPermissionsDialog
            apiKey={editing}
            savePermissions={(keyId, permissions) =>
              setPartnerKeyPermissions(partnerId, keyId, permissions)
            }
            invalidateKey={['partners', partnerId, 'keys']}
            availablePermissions={keys.data.available_permissions}
            operatingMode={partner.operating_mode}
            onDone={() => setEditing(null)}
          />
        )}
      </Dialog>
    </>
  );
}
